"use client"

import Link from "next/link"
import { Stethoscope, ScanSearch, ArrowRight } from "lucide-react"
import { analytics, trackEvent } from "@/lib/analytics"
import { DIAGNOSE_CTA } from "@/lib/blog-diagnose-cta"
import { IDENTIFY_CTA, identifyCtaProps } from "@/lib/identify-cta"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"

/**
 * Footer lawn-action CTAs.
 *
 * Replaces the footer newsletter email form, which the July 2026 post-Hoggie
 * PostHog audit measured at 0 captures across 157 unique impressions in 48h —
 * a dead ask on the site's highest-reach, lowest-intent surface. Instead of a
 * cold email request, route that reach into the two flows that actually convert:
 * the photo diagnose flow (the diagnosis gate converts ~25%) and grass-ID. Email
 * is still captured at every higher-intent surface (tool results, gates, blog
 * reminder widgets), so nothing is lost by dropping it here.
 */
export function FooterLawnCTA({ layout = "stack" }: { layout?: "stack" | "row" }) {
  // `row` sits the two cards side-by-side on md+ for the 2a footer CTA band;
  // `stack` is the original vertical pair. Card markup + tracking are identical.
  const container =
    layout === "row"
      ? "grid gap-2 md:grid-cols-2"
      : "space-y-2"

  // Denominators. Both links shipped without an impression event, so neither has
  // ever had a CTR despite the diagnose card being the single largest source of
  // /diagnose clicks on the site (42 clicks / 28d as of 2026-08-08, out-driving
  // the purpose-built in-content diagnose card's 25 on 2,916 impressions).
  // Per-link rather than per-container: the two carry different cta_location
  // values, a shared impression would make each one's CTR wrong by exactly the
  // other's share, and in the `stack` layout on a short viewport one can be
  // visible while the other is not. This is a footer, so an impression means
  // "scrolled to the bottom", which is a real denominator and not a restatement
  // of the pageview count.
  //
  // VOLUME — read this before writing any query over `cta_surface_shown`.
  // This component renders in the SITE-WIDE footer, so these two events are by
  // far the highest-volume emitters of `cta_surface_shown` on the site. Baseline
  // before they shipped: 10,195 events / 30d against 64,648 pageviews. These add
  // roughly 2 events per footer-reaching pageview, so expect the total to grow
  // several-fold and to be dominated by `footer_lawn_cta`.
  //
  // Judged worth it: the footer diagnose link is the largest single source of
  // /diagnose clicks on the site and has never had a denominator. But it means
  // ANY aggregate over `cta_surface_shown` must filter by `cta_location` (or
  // exclude `surface = 'footer_lawn_cta'`), or it is mostly measuring how many
  // people scrolled to the footer. No saved insight consumed this event at the
  // time of writing, so nothing existing was broken by the change.
  const diagnoseRef = useImpressionEvent<HTMLAnchorElement>(
    DIAGNOSE_CTA.location.footer,
    { surface: "footer_lawn_cta" },
    { emit: analytics.ctaSurfaceShown },
  )
  const identifyRef = useImpressionEvent<HTMLAnchorElement>(
    IDENTIFY_CTA.location.footer,
    { surface: "footer_lawn_cta" },
    { emit: analytics.ctaSurfaceShown },
  )

  return (
    <div className={container}>
      <Link
        ref={diagnoseRef}
        href={DIAGNOSE_CTA.href}
        prefetch={false}
        onClick={() =>
          trackEvent("cta_clicked", {
            cta_location: DIAGNOSE_CTA.location.footer,
            cta_text: "Diagnose my lawn",
          })
        }
        className="group flex items-center gap-3 rounded-xl border border-white/60 bg-white/55 p-3 backdrop-blur-md transition-[transform,background,border-color] duration-200 hover:-translate-y-0.5 hover:border-white/80 hover:bg-white/75"
      >
        <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg border border-white/70 bg-green-50/85 text-green-700">
          <Stethoscope className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-gray-900 group-hover:text-green-700">
            Diagnose my lawn
          </span>
          <span className="block text-xs text-gray-500">Photo in, likely cause and fix out</span>
        </span>
        <ArrowRight className="h-4 w-4 flex-shrink-0 text-gray-400 transition-colors group-hover:text-green-600" />
      </Link>

      <Link
        ref={identifyRef}
        href={IDENTIFY_CTA.href}
        prefetch={false}
        onClick={() =>
          trackEvent("cta_clicked", identifyCtaProps(IDENTIFY_CTA.location.footer))
        }
        className="group flex items-center gap-3 rounded-xl border border-white/60 bg-white/55 p-3 backdrop-blur-md transition-[transform,background,border-color] duration-200 hover:-translate-y-0.5 hover:border-white/80 hover:bg-white/75"
      >
        <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg border border-white/70 bg-green-50/85 text-green-700">
          <ScanSearch className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-gray-900 group-hover:text-green-700">
            {IDENTIFY_CTA.label}
          </span>
          <span className="block text-xs text-gray-500">Get a free personalized care plan</span>
        </span>
        <ArrowRight className="h-4 w-4 flex-shrink-0 text-gray-400 transition-colors group-hover:text-green-600" />
      </Link>
    </div>
  )
}
